import { isMobileDevice, isIOSSafari, isAndroidChrome, scrollIntoViewWithKeyboard } from './mobileUtils';

/**
 * Mobile Focus Manager - Handles mobile-specific focus and keyboard behavior
 */
export class MobileFocusManager {
  constructor() {
    this.isMobile = isMobileDevice();
    this.isIOS = isIOSSafari();
    this.isAndroid = isAndroidChrome();
  }

  /**
   * Focuses an element with mobile keyboard activation
   * @param {HTMLElement} element - Element to focus
   * @param {Object} options - Focus options
   * @returns {Promise<void>}
   */
  async focusWithKeyboard(element, options = {}) {
    if (!element) {
      console.warn('MobileFocusManager: No element provided to focus');
      return;
    }

    const {
      scrollIntoView = true,
      scrollDelay = 200,
      positionCursor = true,
      preventZoom = true
    } = options;

    // For non-mobile devices, just focus normally
    if (!this.isMobile) {
      element.focus();
      return;
    }

    // Prevent double-tap zoom if requested
    if (preventZoom) {
      this.preventDoubleTabZoom(element);
    }

    // Mobile-specific focus sequence
    return new Promise((resolve) => {
      // First focus attempt
      element.focus();
      
      // iOS Safari specific handling
      if (this.isIOS) {
        // iOS needs a click event to properly show keyboard
        setTimeout(() => {
          element.click();
          element.focus();
          
          // Position cursor if it's a contentEditable element
          if (positionCursor && element.contentEditable === 'true') {
            this.positionCursorAtEnd(element);
          }
          
          // Scroll into view after keyboard appears
          if (scrollIntoView) {
            scrollIntoViewWithKeyboard(element, scrollDelay);
          }
          
          resolve();
        }, 100);
      } 
      // Android Chrome specific handling
      else if (this.isAndroid) {
        // Android usually responds well to focus + click
        setTimeout(() => {
          element.click();
          element.focus();
          
          if (positionCursor && element.contentEditable === 'true') {
            this.positionCursorAtEnd(element);
          }
          
          if (scrollIntoView) {
            scrollIntoViewWithKeyboard(element, scrollDelay);
          }
          
          resolve();
        }, 50);
      } 
      // Generic mobile handling
      else {
        setTimeout(() => {
          element.focus();
          
          if (positionCursor && element.contentEditable === 'true') {
            this.positionCursorAtEnd(element);
          }
          
          if (scrollIntoView) {
            scrollIntoViewWithKeyboard(element, scrollDelay);
          }
          
          resolve();
        }, 100);
      }
    });
  }

  /**
   * Positions cursor at the end of a contentEditable element
   * @param {HTMLElement} element - ContentEditable element
   */
  positionCursorAtEnd(element) {
    if (!element || element.contentEditable !== 'true') return;

    try {
      const range = document.createRange();
      const selection = window.getSelection();
      
      range.selectNodeContents(element);
      range.collapse(false); // Collapse to end
      
      selection.removeAllRanges();
      selection.addRange(range);
    } catch (error) {
      console.debug('Failed to position cursor:', error);
    }
  }

  /**
   * Prevents double-tap zoom on an element
   * @param {HTMLElement} element - Element to prevent zoom on
   */
  preventDoubleTabZoom(element) {
    if (!element || !this.isMobile) return;
    
    // Add touch-action CSS to prevent zoom - this is the preferred method
    element.style.touchAction = 'manipulation';
    
    // Only prevent default for multi-touch gestures, not single taps
    element.addEventListener('touchstart', (e) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    }, { passive: false });
  }

  /**
   * Handles blur events with mobile-specific behavior
   * @param {HTMLElement} element - Element that lost focus
   * @param {Function} callback - Callback to execute on blur
   * @param {number} delay - Delay before executing callback
   */
  handleMobileBlur(element, callback, delay = 500) {
    if (!element || !callback) return;

    const blurHandler = (e) => {
      // Check if focus is moving to another element within the same form/container
      const relatedTarget = e.relatedTarget;
      const container = element.closest('form, .reply-composer, .inline-reply-form');
      
      const isBlurringToSameContainer = container && container.contains(relatedTarget);
      
      if (!isBlurringToSameContainer) {
        // Delay callback to allow for potential re-focus
        setTimeout(() => {
          // Double-check that element is still not focused
          if (document.activeElement !== element) {
            callback(e);
          }
        }, delay);
      }
    };

    element.addEventListener('blur', blurHandler);
    
    // Return cleanup function
    return () => {
      element.removeEventListener('blur', blurHandler);
    };
  }

  /**
   * Optimizes touch events for mobile interactions
   * @param {HTMLElement} element - Element to optimize
   * @param {Function} onTap - Callback for tap events
   */
  optimizeTouchEvents(element, onTap) {
    if (!element || !this.isMobile) return;

    let touchStartTime = 0;
    let touchStartPos = { x: 0, y: 0 };

    const handleTouchStart = (e) => {
      touchStartTime = Date.now();
      const touch = e.touches[0];
      touchStartPos = { x: touch.clientX, y: touch.clientY };
      
      // Only prevent default for multi-touch gestures
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = (e) => {
      const touchEndTime = Date.now();
      const touchDuration = touchEndTime - touchStartTime;
      
      // Only trigger if it was a quick tap (not a long press)
      if (touchDuration < 300) {
        const touch = e.changedTouches[0];
        const touchEndPos = { x: touch.clientX, y: touch.clientY };
        
        // Check if touch didn't move much (not a swipe)
        const deltaX = Math.abs(touchEndPos.x - touchStartPos.x);
        const deltaY = Math.abs(touchEndPos.y - touchStartPos.y);
        
        if (deltaX < 10 && deltaY < 10) {
          onTap(e);
        }
      }
    };

    element.addEventListener('touchstart', handleTouchStart, { passive: false });
    element.addEventListener('touchend', handleTouchEnd, { passive: false });

    // Return cleanup function
    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }

  /**
   * Creates a mobile-optimized click handler
   * @param {Function} onClick - Click handler function
   * @returns {Function} Optimized click handler
   */
  createMobileClickHandler(onClick) {
    if (!this.isMobile) {
      return onClick;
    }

    return (e) => {
      // Only prevent default for double-tap scenarios
      if (e.detail === 2) {
        e.preventDefault();
      }
      
      // Only stop propagation for specific elements that need it (not navigation links)
      const target = e.target;
      const isNavigationLink = target.closest('a[href]') || 
                              target.closest('[role="button"]') && target.closest('nav, .navigation, .mobile-nav');
      
      if (!isNavigationLink) {
        e.stopPropagation();
      }
      
      // Execute the original click handler
      onClick(e);
    };
  }

  /**
   * Detects if mobile keyboard is visible and executes callback
   * @param {Function} onKeyboardShow - Callback when keyboard shows
   * @param {Function} onKeyboardHide - Callback when keyboard hides
   * @returns {Function} Cleanup function
   */
  watchKeyboardVisibility(onKeyboardShow, onKeyboardHide) {
    if (!this.isMobile) return () => {};

    let isKeyboardVisible = false;
    
    const checkKeyboard = () => {
      const currentHeight = window.visualViewport ? 
        window.visualViewport.height : 
        window.innerHeight;
      
      const screenHeight = window.screen.height;
      const keyboardThreshold = screenHeight * 0.75;
      const keyboardNowVisible = currentHeight < keyboardThreshold;
      
      if (keyboardNowVisible !== isKeyboardVisible) {
        isKeyboardVisible = keyboardNowVisible;
        
        if (isKeyboardVisible && onKeyboardShow) {
          onKeyboardShow();
        } else if (!isKeyboardVisible && onKeyboardHide) {
          onKeyboardHide();
        }
      }
    };

    // Use visualViewport API if available
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', checkKeyboard);
      return () => {
        window.visualViewport.removeEventListener('resize', checkKeyboard);
      };
    } else {
      // Fallback to window resize
      window.addEventListener('resize', checkKeyboard);
      return () => {
        window.removeEventListener('resize', checkKeyboard);
      };
    }
  }
}

// Export singleton instance
export const mobileFocusManager = new MobileFocusManager();